import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import isSameDay from "date-fns/isSameDay";

import { socket } from "helpers/socket";
import { useHttp } from "hooks/http.hook";
import { MESSAGE_TYPE } from "constants/chatConstants";
import { MESSAGES } from "endpointsConst";
import { playMessageNotification, playSystemNotification } from "@/utils/notificationSounds";

import Message from "../Message";
import DateSeparator from "../DateSeparator";

import styles from "./styles.module.scss";

const MESSAGES_LIMIT = 10;

const Dialog = ({ roomId, companionAvatar, employee }) => {
    const lang = useSelector((state) => state.settings.lang);
    const { request, loading } = useHttp();
    const ref = useRef(null);
    const loader = useRef(null);
    const isFirstTimeLoaded = useRef(false);
    const hasMoreMessages = useRef(true);
    const [messages, setMessages] = useState([]);
    const messagesLenght = useRef(messages.length);
    const roomIdRef = useRef(roomId);
    const isLoadingMore = useRef(false);
    const scrollHeightBeforeLoad = useRef(0);
    const scrollTopBeforeLoad = useRef(0);
    const [isLoadingOldMessages, setIsLoadingOldMessages] = useState(false);

    // Обновляем ref при изменении roomId
    useEffect(() => {
        roomIdRef.current = roomId;
    }, [roomId]);

    const conversationListener = useCallback((msg) => {
        // Проверяем что сообщение принадлежит этому чату
        // Сервер отправляет в комнату conversation_id_user_id, но event общий
        // Поэтому фильтруем по from_user_id и to_user_id
        if (!employee?.id) return;

        const isMyMessage = msg.from_user_id === employee.id || msg.to_user_id === employee.id;
        if (isMyMessage) {
            // Play sound only for incoming messages (from companion, not from me)
            // employee here is the companion/other user in the chat
            if (msg.from_user_id === employee.id) {
                // System messages (deal actions) get a different sound
                if (msg.type === MESSAGE_TYPE.SYSTEM) {
                    playSystemNotification();
                } else {
                    playMessageNotification();
                }
            }
            setMessages((messages) => [msg, ...messages]);
        }
    }, [employee?.id]);

    const loadMessages = useCallback(
        (id, isNewChat) => {
            return request(
                `${MESSAGES}?conversation_id=${id}&offset=${
                    isNewChat ? 0 : messages.length
                }&limit=${MESSAGES_LIMIT}&lang=${lang}`,
                "get",
            )
                .then((res) => {
                    const newMessages = res.data.items;
                    if (isNewChat) {
                        setMessages([...newMessages]);
                        hasMoreMessages.current = newMessages.length >= MESSAGES_LIMIT;
                    } else {
                        setMessages([...messages, ...newMessages]);
                        hasMoreMessages.current = newMessages.length >= MESSAGES_LIMIT;
                    }
                })
                .finally(() => {
                    isFirstTimeLoaded.current = true;
                });
        },
        [request, messages, lang],
    );

    useEffect(() => {
        if (roomId && socket.instance) {
            setMessages([]);
            isFirstTimeLoaded.current = false;
            hasMoreMessages.current = true;
            socket.instance.emit("conversation.subscribe", {
                conversation_id: roomId,
            });
            socket.instance.on("conversation.subscribe", conversationListener);
        }

        return () => {
            if (socket.instance) {
                socket.instance.off("conversation.subscribe", conversationListener);
                if (roomId) {
                    socket.instance.emit("conversation.unsubscribe", {
                        conversation_id: roomId,
                    });
                }
            }
        };
    }, [roomId, conversationListener]);

    // Скролл к последнему сообщению при первой загрузке или новом сообщении
    useEffect(() => {
        if (!ref.current || messages.length === 0) return;

        const isNewMessage = messages.length === messagesLenght.current + 1;
        const isFirstLoad = messagesLenght.current === 0 && messages.length > 0;

        if (isLoadingMore.current) {
            // Даем браузеру время отрендерить все элементы для плавности
            requestAnimationFrame(() => {
                if (!ref.current) return;
                // Восстанавливаем позицию скролла после подгрузки старых сообщений
                const newScrollHeight = ref.current.scrollHeight;
                const heightDiff = newScrollHeight - scrollHeightBeforeLoad.current;
                // Восстанавливаем старую позицию + добавленная высота
                ref.current.scrollTop = scrollTopBeforeLoad.current + heightDiff;
            });
            isLoadingMore.current = false;
        } else if (isFirstLoad || isNewMessage) {
            // Скролл к новому сообщению
            ref.current.scrollTo({
                top: ref.current.scrollHeight,
                behavior: isFirstLoad ? 'auto' : 'smooth',
            });
        }

        messagesLenght.current = messages.length;
    }, [messages]);

    const handleObserver = useCallback(
        (entries) => {
            const target = entries[0];
            if (target.isIntersecting && !loading && hasMoreMessages.current && !isLoadingOldMessages) {
                // Сохраняем текущую высоту и позицию скролла перед загрузкой
                scrollHeightBeforeLoad.current = ref.current?.scrollHeight || 0;
                scrollTopBeforeLoad.current = ref.current?.scrollTop || 0;
                isLoadingMore.current = true;
                setIsLoadingOldMessages(true);
                loadMessages(roomId).finally(() => {
                    setIsLoadingOldMessages(false);
                });
            }
        },
        [loadMessages, roomId, loading],
    );

    useEffect(() => {
        if (!ref.current) return;

        const observerOptions = {
            root: ref.current,
            rootMargin: "100px",
            threshold: 0,
        };

        const observer = new IntersectionObserver(handleObserver, observerOptions);

        const isNewDialog = messages.length === 0 && !isFirstTimeLoaded.current;
        const canLoadMore = hasMoreMessages.current && !loading;

        if (roomId && loader.current && (canLoadMore || isNewDialog)) {
            observer.observe(loader.current);
        }

        return () => observer.disconnect();
    }, [loading, handleObserver, messages.length, roomId]);

    // Рендер сообщений с разделителями по дням
    const renderMessages = () => {
        const elements = [];

        messages.forEach((message, index) => {
            // Добавляем сообщение
            elements.push(
                <Message
                    key={message.id}
                    message={message}
                    companionAvatar={companionAvatar}
                    employee={employee}
                />
            );

            // Проверяем: это последнее сообщение в группе с одной датой?
            const isLastMessage = index === messages.length - 1;
            const hasNextMessage = index < messages.length - 1;
            const nextMessage = hasNextMessage ? messages[index + 1] : null;
            const isDifferentDay = nextMessage && !isSameDay(
                new Date(message.created_at),
                new Date(nextMessage.created_at)
            );

            // Если последнее в группе - добавляем разделитель
            if (isLastMessage || isDifferentDay) {
                elements.push(
                    <li key={`separator-${message.id}`} className={styles.dialog__dateSeparator}>
                        <DateSeparator date={message.created_at} />
                    </li>
                );
            }
        });

        return elements;
    };

    return (
        <div className={styles.dialog} ref={ref}>
            {roomId && (
                <ul className={styles.dialog__list}>
                    {isLoadingOldMessages && (
                        <li className={styles.dialog__loadingIndicator}>
                            <div className={styles.dialog__spinner} />
                        </li>
                    )}
                    {renderMessages()}
                    <li ref={loader} className={styles.dialog__loading} />
                </ul>
            )}
        </div>
    );
};

export default Dialog;
